import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { Kopf } from '../komponenten/Rahmen';

import {
  KANALNAME,
  type Kanal,
  type Kanalart,
  type Kondition,
  type Kunde,
  akte as holeAkte,
  feldSichern,
  kanalAnlegen,
  kanalLoeschen,
  kanalPrimaerSetzen,
  konditionSichern,
  kontaktAnlegen,
  kontaktBeenden,
  mitgliedspruefung,
  zusammenfuehrungRuecknehmen,
} from '../api/kunden';
import {
  werbestandAmAnsprechpartnerSetzen,
  werbestandAmKundenSetzen,
  WERBESTANDNAME,
  type Werbestand,
} from '../api/datenschutz';
import { STATUSNAME as ANGEBOTSSTATUSNAME } from '../api/angebote';
import { STATUSNAME as AUFTRAGSSTATUSNAME } from '../api/auftraege';
import { STATUSNAME as RECHNUNGSSTATUSNAME, ZAHLUNGSSTANDNAME } from '../api/rechnungen';
import { ApiFehler } from '../api/fehler';
import { alsEuro } from '../betrag';
import { heute as heuteIso } from '../datum';
import { markenklasse } from '../marken';
import { statusmarke, Zuordnungsmarke } from '../support/Zuordnung';
import { STATUSMARKE as ANGEBOTSMARKE } from '../angebot/Angebotsliste';
import { STATUSMARKE as AUFTRAGSMARKE } from '../auftrag/Auftragsliste';
import { STATUSMARKE as RECHNUNGSMARKE, ZAHLUNGSMARKE } from '../rechnung/Rechnungen';
import {
  Abschnitt,
  Aktenrumpf,
  Feldraster,
  Feldzeile,
  Kennzahlenleiste,
  Personenkaertchen,
  Reiterleiste,
  Verlauf,
  type Kennzahl,
} from '../komponenten/Akte';
import { kuerzel } from '../komponenten/Rahmen';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Feld } from '../komponenten/Feld';
import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Skelett } from '../start/Widget';
import {
  type Kundenvorgaenge,
  ladeVorgaenge,
  langTag,
  naechsteSchritte,
  hoechsteMahnstufe,
  saldoDerPosten,
  verlaufAus,
} from './kundenvorgaenge';
import { Kontakt, kontaktAusAkte } from './Kundenvorschau';
import { FreieGutschrift } from '../rechnung/Gutschriftmaske';
import { Guthabenhandlung } from '../rechnung/Guthabenhandlung';
import { AktenWiedervorlagen } from './Wiedervorlagen';
import { Gutscheinwerte, Kostentraegerzeile } from '../rechnung/BgZuschuss';
import { type Gutscheinwert, werte as holeWerte } from '../api/bgzuschuss';
import { AbTablet } from '../komponenten/AbTablet';
import { Kundenmarke } from './Kundenmarke';
import { KundeVerweis } from '../komponenten/Kontaktkarte';

type Reiter =
  | 'details'
  | 'kontakte'
  | 'konditionen'
  | 'belege'
  | 'buchungen'
  | 'dokumente'
  /* SUP-11 (CRM-177): nur mit support/sehen. */
  | 'kommunikation'
  /* PRD-38 (CRM-171): nur bei Kostenträgern und mit bgzuschuss/sehen. */
  | 'gutscheinwerte';

/**
 * Der Adresstyp als Wort (KUN-03). Die vier Werte sind durch einen Zusatz
 * festgelegt — sie hier auszuschreiben erfindet nichts, es übersetzt nur.
 */
const ADRESSTYPNAME: Readonly<Record<string, string>> = {
  rechnung: 'Rechnungsanschrift',
  liefer: 'Lieferanschrift',
  besuch: 'Besuchsanschrift',
  post: 'Postanschrift',
};

/**
 * Die Kundenart, wie sie über dem Namen steht (Entwurf: „Firmenkunde ·
 * K-10428"), und das Sinnbild der Aktenkachel.
 */
const KUNDENARTWORT: Readonly<Record<Kunde['kundenart'], string>> = {
  geschaeft: 'Firmenkunde',
  oeffentlich: 'Öffentlicher Auftraggeber',
  privat: 'Privatkunde',
};

const KUNDENARTBILD = {
  geschaeft: 'buildings',
  oeffentlich: 'bank',
  privat: 'user',
} as const;

/**
 * ============================================================================
 * DIE MITGLIEDSNUMMER UND IHRE PRÜFUNG (BUC-98)
 * ============================================================================
 *
 * ZWEI DINGE STEHEN NEBENEINANDER, und sie sind nicht dasselbe: die NUMMER
 * (was der Kunde angegeben hat) und der STAND (was der Anbieter dazu gesagt
 * hat). Wer die Nummer ändert, hat keinen Stand mehr.
 *
 * DREI ANTWORTEN UND NICHT ZWEI. „Nicht erreichbar" ist etwas anderes als
 * „kein Mitglied" (ERR-01): Ein Nein aus einer Störung entzöge dem Kunden
 * einen Preis, auf den er Anspruch hat.
 */
function Mitgliedsfeld({
  kunde,
  beiSichern,
  beiMeldung,
}: {
  readonly kunde: Kunde;
  readonly beiSichern: (feld: string, wert: string | null) => Promise<void>;
  readonly beiMeldung: (meldung: string) => void;
}): JSX.Element {
  const [laeuft, setzeLaeuft] = useState(false);

  const stand = kunde.mitgliedschaft;
  const marke =
    stand === null
      ? null
      : stand.ergebnis === 'mitglied'
        ? { text: 'Mitglied', klasse: 'marke marke--gut' }
        : stand.ergebnis === 'kein_mitglied'
          ? { text: 'kein Mitglied', klasse: 'marke marke--inaktiv' }
          : { text: 'nicht erreichbar', klasse: 'marke marke--warn' };

  return (
    <>
      <Feldzeile
        beschriftung="Mitgliedsnummer"
        kennung="akte-mitgliedsnummer"
        wert={kunde.mitgliedsnummer}
        hilfe="Groß und ohne Leerzeichen gespeichert — so, wie der Anbieter sie führt."
        beiSichern={(w) => beiSichern('mitgliedsnummer', w)}
      />
      <div className="feldzeile">
        <div className="feldzeile__inhalt">
          <span className="feldzeile__beschriftung">Mitgliedschaft</span>
          <span className="feldzeile__wert">
            {kunde.mitgliedsnummer === null ? (
              'Ohne Nummer gibt es nichts zu prüfen.'
            ) : marke === null ? (
              'Zu dieser Nummer liegt noch keine Auskunft vor.'
            ) : (
              <>
                <span className={marke.klasse}>{marke.text}</span> geprüft am{' '}
                {langTag(stand!.geprueftAm)}
                {stand!.gegenstelle === 'attrappe' && ' · Attrappe, kein echter Anbieter'}
              </>
            )}
          </span>
        </div>
        {kunde.mitgliedsnummer !== null && (
          <button
            type="button"
            className="knopf knopf--klein"
            disabled={laeuft}
            onClick={() => {
              setzeLaeuft(true);
              void mitgliedspruefung(kunde.id)
                .then((e) => {
                  beiMeldung(
                    e.ergebnis === 'mitglied'
                      ? 'Die Mitgliedschaft besteht.'
                      : e.ergebnis === 'kein_mitglied'
                        ? 'Der Anbieter kennt diese Nummer nicht.'
                        : 'Der Anbieter antwortet gerade nicht — das ist kein Nein. '
                          + 'Versuchen Sie es später erneut.',
                  );
                })
                .finally(() => setzeLaeuft(false));
            }}
          >
            {laeuft ? 'Wird geprüft …' : 'Prüfen'}
          </button>
        )}
      </div>
    </>
  );
}

/**
 * M-13: einen Ansprechpartner anlegen (KUN-08).
 *
 * Im selben Reiter und nicht in einer eigenen Maske: Ein Ansprechpartner ist
 * kein Vorgang, sondern eine Angabe am Kunden.
 */
function NeuerKontakt({
  kunde,
  beiAngelegt,
  beiFehler,
}: {
  readonly kunde: string;
  readonly beiAngelegt: () => void;
  readonly beiFehler: (fehler: ApiFehler) => void;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  const [vorname, setzeVorname] = useState('');
  const [nachname, setzeNachname] = useState('');
  const [funktion, setzeFunktion] = useState('');
  const [email, setzeEmail] = useState('');
  const [rechnung, setzeRechnung] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);

  if (!offen) {
    return (
      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={() => setzeOffen(true)}>
          Ansprechpartner hinzufügen
        </button>
      </div>
    );
  }

  return (
    <form
      className="unterblock"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setzeLaeuft(true);
        void kontaktAnlegen(kunde, {
          person: {
            anrede: null,
            titel: null,
            vorname: vorname === '' ? null : vorname,
            nachname,
            geburtsdatum: null,
          },
          rolle: 'ansprechpartner',
          funktion: funktion === '' ? null : funktion,
          abteilung: null,
          rechnungsempfaenger: rechnung,
          // KUN-04: Die E-Mail ist ein Kanal an der Kontaktrolle und keine
          // Spalte. Der erste seiner Art wird der primaere — das entscheidet
          // der Server.
          kanaele:
            email.trim() === ''
              ? []
              : [{ art: 'email' as const, wert: email.trim(), bezeichnung: null }],
        })
          .then(() => {
            setzeOffen(false);
            setzeVorname('');
            setzeNachname('');
            setzeFunktion('');
            setzeEmail('');
            setzeRechnung(false);
            beiAngelegt();
          })
          .catch((u: unknown) => beiFehler(u as ApiFehler))
          .finally(() => setzeLaeuft(false));
      }}
    >
      <div className="feldraster">
        <Feld beschriftung="Vorname" wert={vorname} aendern={setzeVorname} />
        <Feld beschriftung="Nachname" wert={nachname} aendern={setzeNachname} pflicht />
        <Feld beschriftung="Funktion" wert={funktion} aendern={setzeFunktion} />
        <Feld beschriftung="E-Mail" wert={email} aendern={setzeEmail} />
      </div>
      <label className="liste__schalter">
        <input
          type="checkbox"
          checked={rechnung}
          onChange={(e) => setzeRechnung(e.target.checked)}
        />
        Rechnungsempfänger
      </label>
      <div className="knopfreihe knopfreihe--abschluss">
        <button type="button" className="knopf" onClick={() => setzeOffen(false)}>
          Abbrechen
        </button>
        <button type="submit" className="knopf knopf--haupt" disabled={laeuft || nachname === ''}>
          Hinzufügen
        </button>
      </div>
    </form>
  );
}

/**
 * M-14 Konditionen (KUN-10, SYS-03).
 *
 * Kreditlimit und Bankdaten erscheinen NUR, wenn die API sie mitgeschickt hat
 * — geprueft mit `in` und nicht auf einen Wert. Wer sie nicht sehen darf, sieht
 * hier auch kein gesperrtes Feld: Ein graues Feld verraet, dass es einen Wert
 * gibt (M-12).
 *
 * Gesichert wird beim Verlassen des Feldes, wie an den Stammdaten. Der
 * Bearbeitungsstand geht mit; bei einem Konflikt erscheint die Meldung nach
 * ERR-13 und die Anzeige wird aktualisiert.
 */
function Konditionen({
  kunde,
  kondition,
  beiGesichert,
  beiFehler,
}: {
  readonly kunde: string;
  readonly kondition: Kondition;
  readonly beiGesichert: () => Promise<void> | void;
  readonly beiFehler: (fehler: ApiFehler) => void;
}): JSX.Element {
  const sichern = async (feld: string, wert: string | number | null): Promise<void> => {
    try {
      await konditionSichern(kunde, { [feld]: wert }, kondition.stand);
    } catch (u) {
      beiFehler(u as ApiFehler);
    }
    await beiGesichert();
  };

  return (
    <div className="tafel__abschnitte">
      {/* M-26: die Preispflege hat ihre eigene Maske. */}
      <p className="hilfe">
        Änderungen werden beim Verlassen des Feldes gesichert. Preise und Preislisten gehören
        der Buchhaltung und stehen unter „Produkte und Preise".
      </p>

      <Abschnitt titel="Zahlung und Versand">
        <Feldraster>
          <Feldzeile
            beschriftung="Zahlungsziel in Tagen"
            kennung="akte-zahlungsziel"
            wert={String(kondition.zahlungsziel)}
            beiSichern={(w) => sichern('zahlungsziel', w === null ? 30 : Number(w))}
          />
          <Feldzeile
            beschriftung="Rabattgruppe"
            kennung="akte-rabattgruppe"
            wert={kondition.rabattgruppe}
            beiSichern={(w) => sichern('rabattgruppe', w)}
          />
          <Feldzeile
            beschriftung="Versandart"
            kennung="akte-versandart"
            wert={kondition.versandart}
            beiSichern={(w) => sichern('versandart', w)}
          />
          <Feldzeile
            beschriftung="Ausgabeformat der Rechnung"
            kennung="kd-format"
            wert={kondition.ausgabeformat}
            optionen={[
              { wert: 'pdf', text: 'PDF' },
              { wert: 'zugferd', text: 'ZUGFeRD' },
              { wert: 'xrechnung', text: 'XRechnung' },
            ]}
            hilfe={
              'Eigenschaft des Kunden. Beim Erzeugen der Rechnung wird nicht mehr gefragt. '
              + 'XRechnung gilt für öffentliche Auftraggeber — sie verlangt eine Leitweg-ID. '
              + 'ZUGFeRD ist ein PDF mit eingebetteten Rechnungsdaten und für Firmenkunden die '
              + 'verträgliche Wahl. PDF ist ein Beleg zum Lesen, ohne strukturierte Daten.'
            }
            beiSichern={(w) => sichern('ausgabeformat', w)}
          />
          <Feldzeile
            beschriftung="Zahlungsart"
            kennung="kd-zahlungsart"
            wert={kondition.zahlungsart}
            optionen={[
              { wert: 'rechnung', text: 'Rechnung' },
              { wert: 'zahlungslink', text: 'Zahlungslink' },
              { wert: 'lastschrift', text: 'Lastschrift' },
            ]}
            /* RNG-33, BUC-77 */
            hilfe="Barzahlung gibt es nicht."
            beiSichern={(w) => sichern('zahlungsart', w)}
          />
          {/* SYS-03: `in` und nicht `!== null`. Fehlt das Feld, erscheint hier
              nichts — auch keine gesperrte Zeile. */}
          {/* AA-004 */}
          {'kreditlimit' in kondition && (
            <Feldzeile
              beschriftung="Kreditlimit in Euro"
              kennung="akte-kreditlimit"
              wert={kondition.kreditlimit ?? null}
              /* SYS-03: feldbezogene Berechtigung. */
              hilfe="Sichtbar für Buchhaltung, Geschäftsleitung und Vertrieb."
              beiSichern={(w) => sichern('kreditlimit', w)}
            />
          )}
        </Feldraster>
      </Abschnitt>

      {'iban' in kondition && (
        <Abschnitt titel="Bankverbindung">
          <Feldraster>
            <Feldzeile
              beschriftung="IBAN"
              kennung="akte-iban"
              wert={kondition.iban ?? null}
              beiSichern={(w) => sichern('iban', w)}
            />
            <Feldzeile
              beschriftung="BIC"
              kennung="akte-bic"
              wert={kondition.bic ?? null}
              beiSichern={(w) => sichern('bic', w)}
            />
            <Feldzeile
              beschriftung="Kontoinhaber"
              kennung="akte-kontoinhaber"
              wert={kondition.kontoinhaber ?? null}
              beiSichern={(w) => sichern('kontoinhaber', w)}
            />
          </Feldraster>
          {/* LOG-05, LOG-02 */}
          <p className="feld__hilfe">
            Jede Einsicht in Bankdaten erzeugt einen Zugriffsprotokolleintrag.
          </p>
        </Abschnitt>
      )}
    </div>
  );
}

/** LEG-12: der Stand in Worten, wie ihn der Abschnitt am Kunden nennt. */
const WERBESTANDTEXT: Readonly<Record<Werbestand, string>> = {
  einwilligung: 'Einwilligung erteilt',
  ausnahme: 'zulässiger Ausnahmetatbestand (§ 7 Abs. 3 UWG)',
  widerspruch: 'Widerspruch — keine werbliche Ansprache',
};

/** LEG-12: Ohne Eintrag wird nicht geworben — die Zeile sagt es, statt leer zu bleiben. */
const OHNE_WERBESTAND = 'nichts dokumentiert — es wird nicht geworben';

/**
 * DEN WERBESTAND DOKUMENTIEREN (LEG-12) — ein Fenster mit Stand, Quelle und
 * Bemerkung, dieselbe Bedienung am Kunden und am Ansprechpartner. Wohin der
 * Stand geht, sagt `absenden`.
 *
 * Der Fehler bleibt im Fenster (Auftrag CRM-173): Die Meldung der Seite
 * stünde hinter dem Schleier, und die Eingabe ginge mit dem Schließen verloren.
 */
function WerbestandDialog({
  vorgabe,
  unterzeile,
  absenden,
  beiSchliessen,
  beiDokumentiert,
}: {
  readonly vorgabe: Werbestand;
  readonly unterzeile: string;
  readonly absenden: (stand: Werbestand, quelle: string, bemerkung?: string) => Promise<unknown>;
  readonly beiSchliessen: () => void;
  readonly beiDokumentiert: () => void;
}): JSX.Element {
  const [stand, setzeStand] = useState<Werbestand>(vorgabe);
  const [quelle, setzeQuelle] = useState('');
  const [bemerkung, setzeBemerkung] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const kennung = useId();

  const amFeld = (feld: string): readonly ApiFehler['verstoesse'][number][] =>
    fehler?.alleZumFeld(feld) ?? [];

  return (
    <Dialog
      titel="Werbestand dokumentieren"
      unterzeile={unterzeile}
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setzeLaeuft(true);
          setzeFehler(null);
          void absenden(
            stand,
            quelle.trim(),
            bemerkung.trim() === '' ? undefined : bemerkung.trim(),
          )
            .then(beiDokumentiert)
            .catch((u: unknown) => {
              if (!(u instanceof ApiFehler)) throw u;
              setzeFehler(u);
            })
            .finally(() => setzeLaeuft(false));
        }}
      >
        {fehler !== null && <Meldung fehler={fehler} gezeigteFelder={['quelle', 'bemerkung']} />}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-stand`}>
            Stand
          </label>
          <select
            id={`${kennung}-stand`}
            className="feld__eingabe"
            value={stand}
            onChange={(e) => {
              setzeStand(e.target.value as Werbestand);
            }}
          >
            {(['einwilligung', 'ausnahme', 'widerspruch'] as const).map((s) => (
              <option key={s} value={s}>
                {WERBESTANDNAME[s]}
              </option>
            ))}
          </select>
          {/* LEG-12: Der Widerspruch schlägt jeden anderen Stand, auf jedem Weg. */}
          {stand === 'widerspruch' && (
            <p className="feld__hilfe">
              Wirkt kanalübergreifend — für Post, Mail und Telefon zugleich.
            </p>
          )}
        </div>
        {/* LEG-12: Ohne Quelle ist der Eintrag im Streitfall keine Auskunft. */}
        <Feld
          beschriftung="Quelle"
          wert={quelle}
          aendern={setzeQuelle}
          pflicht
          hinweisText="Anmeldeformular vom 09.09.2026"
          verstoesse={amFeld('quelle')}
        />
        <Feld
          beschriftung="Bemerkung"
          wert={bemerkung}
          aendern={setzeBemerkung}
          verstoesse={amFeld('bemerkung')}
        />
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" onClick={beiSchliessen}>
              Abbrechen
            </button>
          }
        >
          <button
            type="submit"
            className="knopf knopf--haupt"
            disabled={laeuft || quelle.trim() === ''}
          >
            Dokumentieren
          </button>
        </Abschlussreihe>
      </form>
    </Dialog>
  );
}

/**
 * DER ABSCHNITT „WERBLICHE ANSPRACHE" (LEG-12) — am Kunden und am
 * Ansprechpartner derselbe (Auftrag CRM-173): der Stand lesend im Feldraster
 * wie jeder Abschnitt der Akte, darunter „Werbestand dokumentieren", das das
 * Fenster öffnet. Was den Stand erklärt, steht unter dem Hilfe-Symbol (ÄA-46).
 */
function Werbeabschnitt({
  titel,
  zusatz,
  unterzeile,
  vorgabe,
  absenden,
  beiAenderung,
  beiMeldung,
  children,
}: {
  readonly titel: string;
  readonly zusatz: string;
  /** Wessen Stand das Fenster dokumentiert. */
  readonly unterzeile: string;
  readonly vorgabe: Werbestand;
  readonly absenden: (stand: Werbestand, quelle: string, bemerkung?: string) => Promise<unknown>;
  readonly beiAenderung: () => void;
  readonly beiMeldung: (text: string) => void;
  /** Das Feldraster mit dem Stand. */
  readonly children: ReactNode;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);

  return (
    <Abschnitt titel={titel} zusatz={zusatz}>
      {children}
      <div className="knopfreihe">
        <button
          type="button"
          className="knopf"
          onClick={() => {
            setzeOffen(true);
          }}
        >
          Werbestand dokumentieren
        </button>
      </div>
      {offen && (
        <WerbestandDialog
          vorgabe={vorgabe}
          unterzeile={unterzeile}
          absenden={absenden}
          beiSchliessen={() => {
            setzeOffen(false);
          }}
          beiDokumentiert={() => {
            setzeOffen(false);
            beiMeldung('Der Werbestand ist dokumentiert.');
            beiAenderung();
          }}
        />
      )}
    </Abschnitt>
  );
}

/**
 * DIE DOKUMENTIERTE WERBEEINWILLIGUNG (LEG-12).
 *
 *   Einwilligung   ausdrücklich erteilt (Art. 6 Abs. 1 lit. a DSGVO)
 *   Ausnahme       zulässig ohne Einwilligung — § 7 Abs. 3 UWG
 *   Widerspruch    Art. 21 Abs. 2 DSGVO, § 7 Abs. 2 UWG; wirkt über ALLE
 *                  Kanäle und schlägt beides andere.
 *
 * OHNE EINTRAG WIRD NICHT GEWORBEN, und der Abschnitt sagt das, statt ein
 * leeres Feld zu zeigen. DIE QUELLE IST PFLICHT: „Widerspruch" allein ist im
 * Streitfall keine Auskunft.
 */
function Werbeeinwilligung({
  kunde,
  beiAenderung,
  beiMeldung,
}: {
  readonly kunde: Kunde;
  readonly beiAenderung: () => void;
  readonly beiMeldung: (text: string) => void;
}): JSX.Element {
  /* `?? null`: Ein Aufrufer, der das Feld noch nicht kennt, bekommt „nichts
     dokumentiert" — die sichere Antwort nach LEG-12. */
  const erlaubnis = kunde.werbeerlaubnis ?? null;

  return (
    <Werbeabschnitt
      titel="Werbliche Ansprache"
      zusatz={
        erlaubnis === null
          ? 'nichts dokumentiert'
          : `seit ${new Date(erlaubnis.seit).toLocaleDateString('de-DE')}`
      }
      unterzeile={kunde.name}
      vorgabe={erlaubnis?.stand ?? 'einwilligung'}
      absenden={(stand, quelle, bemerkung) =>
        werbestandAmKundenSetzen(kunde.id, stand, quelle, bemerkung)
      }
      beiAenderung={beiAenderung}
      beiMeldung={beiMeldung}
    >
      <Feldraster>
        {erlaubnis === null ? (
          <Feldzeile beschriftung="Stand" wert={OHNE_WERBESTAND} />
        ) : (
          <>
            <Feldzeile beschriftung="Stand" wert={WERBESTANDTEXT[erlaubnis.stand]} />
            <Feldzeile beschriftung="Quelle" wert={erlaubnis.quelle} />
            {erlaubnis.bemerkung !== null && (
              <Feldzeile beschriftung="Bemerkung" wert={erlaubnis.bemerkung} />
            )}
            <Feldzeile
              beschriftung="Hängt an"
              wert={
                erlaubnis.traeger === 'person'
                  ? 'der Person — gilt privat, auch für ihre weiteren Konten, nicht als Ansprechpartner einer Firma'
                  : 'diesem Kundenkonto'
              }
            />
          </>
        )}
      </Feldraster>
    </Werbeabschnitt>
  );
}

/**
 * Die Kommunikationskanäle eines Kunden (KUN-04, M-12).
 *
 *   * DER PRIMÄRE KANAL STEHT ALS MARKE DA und ist mit einem Klick zu
 *     wechseln: ANG-14 versendet Angebote an die primäre E-Mail.
 *   * Ein Kanal wird ENTFERNT und nicht bearbeitet: Eine geänderte Rufnummer
 *     ist eine andere Rufnummer (DAT-25).
 *   * DAS ENTFERNEN FRAGT IM POP-UP NACH (CRM-104): Es wirkt sofort, und
 *     zurück kommt der Kanal nur durch neue Erfassung. „als primär" fragt
 *     nicht — ein zweiter Klick an einem anderen Kanal stellt es um.
 */
function Kommunikation({
  kunde,
  kanaele,
  beiAenderung,
  beiFehler,
}: {
  readonly kunde: string;
  readonly kanaele: readonly Kanal[];
  readonly beiAenderung: () => void;
  readonly beiFehler: (fehler: ApiFehler) => void;
}): JSX.Element {
  const [art, setzeArt] = useState<Kanalart>('email');
  const [wert, setzeWert] = useState('');
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [zuEntfernen, setzeZuEntfernen] = useState<Kanal | null>(null);

  const melde = (u: unknown): void => beiFehler(u as ApiFehler);

  return (
    <Abschnitt titel="Kommunikation" zusatz={`${kanaele.length} Kanäle`}>
      <ul className="kanalliste">
        {kanaele.length === 0 && <li className="feld__hilfe">Noch kein Kanal erfasst.</li>}
        {kanaele.map((c) => (
          <li key={c.id}>
            <span className="kanalliste__art">{KANALNAME[c.art]}</span>
            <span className="kanalliste__wert">
              {c.wert}
              {c.bezeichnung !== null && (
                <span className="kanalliste__name"> · {c.bezeichnung}</span>
              )}
            </span>
            {c.istPrimaer ? (
              <span className="marke marke--akzent">primär</span>
            ) : (
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => {
                  void kanalPrimaerSetzen(kunde, c.id).then(beiAenderung).catch(melde);
                }}
              >
                als primär
              </button>
            )}
            <button
              type="button"
              className="knopf knopf--eingriff knopf--klein"
              onClick={() => {
                setzeZuEntfernen(c);
              }}
            >
              Entfernen …
            </button>
          </li>
        ))}
      </ul>

      {zuEntfernen !== null && (
        <Rueckfrage
          frage="Kanal entfernen?"
          unterzeile={`${KANALNAME[zuEntfernen.art]} · ${zuEntfernen.wert}`}
          tat="Entfernen"
          beiBestaetigen={async () => {
            // Ein Fehler bleibt im Pop-up; die Seite meldet ihn nicht ein zweites Mal.
            await kanalLoeschen(kunde, zuEntfernen.id);
            setzeZuEntfernen(null);
            beiAenderung();
          }}
          beiSchliessen={() => {
            setzeZuEntfernen(null);
          }}
        >
          <p>
            {zuEntfernen.wert} verschwindet aus der Akte; zurück kommt der Kanal nur durch neue
            Erfassung.
            {/* ANG-14: Angebote gehen an die primäre E-Mail. */}
            {zuEntfernen.istPrimaer && ' Er ist als primär gekennzeichnet.'}
          </p>
        </Rueckfrage>
      )}

      <form
        className="kanalzeile"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setzeLaeuft(true);
          void kanalAnlegen(kunde, {
            art,
            wert: wert.trim(),
            bezeichnung: bezeichnung.trim() === '' ? null : bezeichnung.trim(),
            kontaktrolle: null,
          })
            .then(() => {
              setzeWert('');
              setzeBezeichnung('');
              beiAenderung();
            })
            .catch(melde)
            .finally(() => setzeLaeuft(false));
        }}
      >
        <select
          value={art}
          onChange={(e) => setzeArt(e.target.value as Kanalart)}
          aria-label="Kanalart"
        >
          {(['email', 'telefon', 'mobil', 'website'] as const).map((a) => (
            <option key={a} value={a}>
              {KANALNAME[a]}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={wert}
          onChange={(e) => setzeWert(e.target.value)}
          placeholder={art === 'email' ? 'name@firma.de' : '0511 12345'}
          aria-label="Wert des Kanals"
        />
        <input
          type="text"
          value={bezeichnung}
          onChange={(e) => setzeBezeichnung(e.target.value)}
          placeholder="Bezeichnung"
          aria-label="Bezeichnung des Kanals"
        />
        <button type="submit" className="knopf" disabled={laeuft || wert.trim() === ''}>
          Hinzufügen
        </button>
      </form>
    </Abschnitt>
  );
}

/*
 * CRM-104: Was sofort wirkt und sich in der Akte nicht umkehren lässt, fragt
 * erst im Pop-up nach. Offen ist höchstens eine Rückfrage.
 */
type Nachfrage =
  | { readonly art: 'ruecknahme' }
  | { readonly art: 'ausgeschieden'; readonly kontakt: string; readonly name: string };

/** „nicht lesbar" und „leer" sind zwei Auskünfte (siehe `kundenvorgaenge.ts`). */
function Unlesbar({ was }: { readonly was: string }): JSX.Element {
  return <p className="feld__hilfe">{was} lassen sich hier nicht anzeigen.</p>;
}

/** Stufe 1 der Kundenakte (Auftrag CRM-165): eine Karte mit Zeilen, wie die Akte gleich kommt. */
function Aktenskelett(): JSX.Element {
  return (
    <section className="karte">
      <Skelett art="liste" />
    </section>
  );
}

/**
 * M-12 KUNDENAKTE NACH ENTWURF V2 (ÄA-41) — die Referenz für alle Akten.
 *
 *   * AKTENKOPF auf dem Grund: Sinnbildkachel nach Kundenart, „Firmenkunde ·
 *     K-…" in Versalien, der Name, rechts die Handlungen — die Haupthandlung
 *     blau ganz rechts. Darunter die Kennzahlenleiste.
 *   * LINKS die Karte mit Reitern und Zählern: Details in aufklappbaren
 *     Abschnitten mit Feldraster und Stift, Kontakte als Personenkärtchen,
 *     Konditionen, Belege und Buchungen als Rastertabellen, Dokumente.
 *   * RECHTS die Aktivität (KUN-14): „Nächste Schritte" und „Verlauf" aus den
 *     Vorgängen, die das CRM kennt (`kundenvorgaenge.ts`).
 *
 * FELDBEZOGENE BERECHTIGUNG (SYS-03): Kreditlimit und Bankdaten werden
 * AUSGEBLENDET, nicht gesperrt angezeigt — die API lässt das Feld weg.
 */
export function Kundenakte({
  id,
  meldung,
  beiKopf,
  beiListe,
  beiZusammenfuehren,
  beiNeuesAngebot,
  beiNeuerAuftrag,
  beiNeueRechnung,
  beiAngebot,
  beiAuftrag,
  beiRechnung,
  beiBuchung,
  darfFreieGutschrift = false,
  darfBuchen = false,
  bgKennzeichen,
  wiedervorlagen,
  kommunikation,
  zurKunde,
}: {
  readonly id: string;
  /** Rueckmeldung aus einer vorangegangenen Maske, etwa nach M-18. */
  readonly meldung?: string | undefined;
  readonly beiKopf: (kopf: Kopf) => void;
  readonly beiListe: () => void;
  readonly beiZusammenfuehren?: () => void;
  /** ANG-01: ein Angebot für diesen Kunden — nur mit `angebot/anlegen`. */
  readonly beiNeuesAngebot?: () => void;
  /** AUF-01: ein Auftrag ohne Angebot — nur mit `auftrag/anlegen`. */
  readonly beiNeuerAuftrag?: () => void;
  /** BUC-75, M-61 (CRM-152): Auftrag anlegen und gleich abrechnen. */
  readonly beiNeueRechnung?: () => void;
  readonly beiAngebot?: (id: string) => void;
  readonly beiAuftrag?: (id: string) => void;
  readonly beiRechnung?: (id: string) => void;
  readonly beiBuchung?: (nummer: string) => void;
  /** RNG-11: die freie Gutschrift — nur mit `rechnung/stornieren`. */
  readonly darfFreieGutschrift?: boolean;
  /** DAT-12: Guthaben erstatten und verrechnen — das Recht des Zahlungsvermerks. */
  readonly darfBuchen?: boolean;
  /**
   * PRD-38, E-74 (CRM-157, CRM-162, CRM-163): die Art des Kostenträgers und seine
   * Gutscheinwerte — sehen mit bgzuschuss/sehen, setzen und pflegen mit
   * kunde/aendern oder kunde/anlegen. Die Art steht in den Stammdaten, die
   * Werte seit CRM-171 im eigenen Reiter.
   */
  readonly bgKennzeichen?: { readonly darfVerwalten: boolean };
  /** KUN-15: die Wiedervorlagen in der Aktivitätsspalte — anlegen mit `kunde/aendern`. */
  readonly wiedervorlagen?: { readonly ich: string; readonly darfAendern: boolean } | undefined;
  /** SUP-11: die E-Mail-Vorgänge des Kunden — nur mit support/sehen; der Klick öffnet den Posteingang. */
  readonly kommunikation?: { readonly beiVorgang: (vorgang: string, postfach: string) => void } | undefined;
  /** Auftrag CRM-181: die Kontaktkarte am Ansprechpartner — der Weg zur Akte, gesetzt nur mit kunde/sehen. */
  readonly zurKunde?: ((id: string) => void) | undefined;
}): JSX.Element {
  const [kunde, setzeKunde] = useState<Kunde | undefined>(undefined);
  const [reiter, setzeReiter] = useState<Reiter>('details');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [vorgaenge, setzeVorgaenge] = useState<Kundenvorgaenge | undefined>(undefined);
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);
  /** LEG-12: der Ansprechpartner, dessen Werbestand gerade gepflegt wird. */
  const [werbeKontakt, setzeWerbeKontakt] = useState<string | null>(null);
  const [freieGutschrift, setzeFreieGutschrift] = useState(false);

  /* Nach einer Handlung: neu holen, der Fehler steht in der Akte. */
  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeKunde(await holeAkte(id));
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [id]);

  /* Auftrag CRM-165: drei Stufen statt „wird geladen"; eine andere Akte laedt neu und bricht die alte ab. */
  const geladen = useLadestufe((signal) => holeAkte(id, signal), id);
  const ladefehler = geladen.fehler instanceof ApiFehler ? geladen.fehler : undefined;
  useEffect(() => {
    if (geladen.daten === undefined) return;
    setzeKunde(geladen.daten);
    setzeFehler(undefined);
  }, [geladen.daten]);
  useEffect(() => {
    if (meldung !== undefined) setzeRueckmeldung(meldung);
  }, [meldung]);

  /* Die Vorgänge hängen an Kennung und Nummer, nicht an jedem Feld der Akte. */
  const kundennummer = kunde?.kundennummer;
  useEffect(() => {
    if (kundennummer === undefined) return;
    let abgebrochen = false;
    void ladeVorgaenge({ id, kundennummer }).then((v) => {
      if (!abgebrochen) setzeVorgaenge(v);
    });
    return () => {
      abgebrochen = true;
    };
  }, [id, kundennummer]);

  /*
   * PRD-38 (CRM-171): der Reiter „Gutscheinwerte" — nur bei einem Kostenträger
   * und mit bgzuschuss/sehen. Der Zähler nennt die Werte, die angeboten
   * werden (veraltete nicht, wie ausgeschiedene Kontakte); er steht, bevor der
   * Reiter geöffnet ist, und folgt jeder Änderung darin.
   */
  const zeigtWerte = kunde?.kostentraeger != null && bgKennzeichen !== undefined;
  const [wertezahl, setzeWertezahl] = useState<number | undefined>(undefined);
  const zaehleWerte = useCallback((liste: readonly Gutscheinwert[]): void => {
    setzeWertezahl(liste.filter((w) => w.aktiv).length);
  }, []);
  useEffect(() => {
    if (!zeigtWerte) {
      setzeWertezahl(undefined);
      setzeReiter((r) => (r === 'gutscheinwerte' ? 'details' : r));
      return;
    }
    let abgebrochen = false;
    holeWerte(id)
      .then((liste) => {
        if (!abgebrochen) zaehleWerte(liste);
      })
      .catch(() => {
        /* Ohne Zähler bleibt der Reiter bedienbar; den Fehler zeigt er selbst. */
      });
    return () => {
      abgebrochen = true;
    };
  }, [id, zeigtWerte, zaehleWerte]);

  /** Die Standardanschrift in einer Zeile — die Rechnungsanschrift zuerst. */
  const anschrift = (k: Kunde): string => {
    const adresse =
      k.adressen.find((a) => a.standard && a.typ === 'rechnung')
      ?? k.adressen.find((a) => a.standard)
      ?? k.adressen[0];
    return adresse === undefined ? '' : `${adresse.strasse}, ${adresse.plz} ${adresse.ort}`;
  };

  const ziele = useMemo(
    () => ({
      ...(beiAngebot === undefined ? {} : { angebot: beiAngebot }),
      ...(beiAuftrag === undefined ? {} : { auftrag: beiAuftrag }),
      ...(beiRechnung === undefined ? {} : { rechnung: beiRechnung }),
      ...(beiBuchung === undefined ? {} : { buchung: beiBuchung }),
      ...(kommunikation === undefined ? {} : { vorgang: kommunikation.beiVorgang }),
    }),
    [beiAngebot, beiAuftrag, beiRechnung, beiBuchung, kommunikation],
  );

  const heute = heuteIso();
  const schritte = vorgaenge === undefined ? [] : naechsteSchritte(vorgaenge, heute, ziele);
  const verlauf = vorgaenge === undefined ? [] : verlaufAus(vorgaenge, heute, ziele);

  /*
   * DIE RÜCKRUFE DES KOPFES STEHEN IN EINEM REF UND NICHT IN DEN
   * ABHÄNGIGKEITEN. Die Arbeitsfläche reicht sie als Pfeilfunktionen herein,
   * also bei jedem Bild neu; stünden sie in der Liste, setzte jeder Kopf ein
   * neues Bild und jedes Bild einen neuen Kopf. Ob es sie GIBT, zählt dagegen —
   * das steht als Wahrheitswert darin.
   */
  const rueckrufe = useRef({ beiListe, beiZusammenfuehren, beiNeuesAngebot, beiNeuerAuftrag, beiNeueRechnung });
  rueckrufe.current = { beiListe, beiZusammenfuehren, beiNeuesAngebot, beiNeuerAuftrag, beiNeueRechnung };
  const kannZusammenfuehren = beiZusammenfuehren !== undefined;
  const kannAngebot = beiNeuesAngebot !== undefined;
  const kannAuftrag = beiNeuerAuftrag !== undefined;
  const kannRechnung = beiNeueRechnung !== undefined;

  /* Der erste Ansprechpartner, der noch dabei ist — der Entwurf nennt ihn im Kopf. */
  const ansprechpartner = kunde?.kontakte.find((k) => !k.ausgeschieden);

  useEffect(() => {
    if (kunde === undefined) return;

    const rahmen = vorgaenge?.rahmen?.filter((r) => r.status === 'aktiv') ?? [];
    const posten = vorgaenge?.offenePosten;
    /* BP-31, Auftrag CRM-113: ein Guthaben hat keine Zahlungsfrist. */
    const forderungen = posten?.filter((p) => p.art !== 'guthaben');
    const naechsteFrist = forderungen?.map((p) => p.faelligAm).sort()[0];

    const zahlen: Kennzahl[] = [
      {
        beschriftung: 'Ansprechpartner',
        wert:
          ansprechpartner === undefined ? (
            '—'
          ) : (
            <KundeVerweis kundeId={kunde.id} ansprechpartnerId={ansprechpartner.id} zurAkte={zurKunde}>
              <button type="button" className="knopf knopf--verweis" onClick={() => setzeReiter('kontakte')}>
                {ansprechpartner.person.vorname !== null ? `${ansprechpartner.person.vorname} ` : ''}
                {ansprechpartner.person.nachname}
              </button>
            </KundeVerweis>
          ),
      },
      { beschriftung: 'Betreuung', wert: kunde.betreuerName ?? '—' },
      ...(vorgaenge?.rahmen === undefined
        ? []
        : [
            {
              beschriftung: 'Rahmenvertrag',
              wert:
                rahmen.length === 0
                  ? 'keiner'
                  : /* Entwurf: „bis 12/2027". */
                    `bis ${(() => {
                      const [jahr, monat] = rahmen.map((r) => r.laufzeitBis).sort().reverse()[0]!.split('-');
                      return `${monat}/${jahr}`;
                    })()}`,
            },
          ]),
      ...(posten === undefined
        ? []
        : [
            { beschriftung: 'Offene Posten', wert: String(posten.length) },
            { beschriftung: 'Saldo', wert: alsEuro(saldoDerPosten(posten)) },
            {
              beschriftung: 'Nächste Zahlungsfrist',
              wert: naechsteFrist === undefined ? '—' : langTag(naechsteFrist),
              frist: naechsteFrist !== undefined,
            },
            /* RNG-25: die höchste Mahnstufe der offenen Posten, aus Navision. */
            ...(hoechsteMahnstufe(posten) === null
              ? []
              : [{ beschriftung: 'Mahnstufe', wert: `${String(hoechsteMahnstufe(posten))}. Mahnung` }]),
          ]),
    ];

    beiKopf({
      kicker: `${KUNDENARTWORT[kunde.kundenart]} · ${kunde.kundennummer}`,
      titel: kunde.name,
      ...(anschrift(kunde) === '' ? {} : { unterzeile: anschrift(kunde) }),
      sinnbild: KUNDENARTBILD[kunde.kundenart],
      kennzeichen: (
        <>
          <Kundenmarke marke={kunde.marke} art={kunde.kostentraeger?.bezeichnung} />
          {kunde.gesperrt && <span className="marke marke--gesperrt">gesperrt</span>}
        </>
      ),
      kennzahlen: <Kennzahlenleiste zahlen={zahlen} />,
      /*
        Wunsch des Nutzers (05.10.2026, Auftrag CRM-56): „beim hover über den
        Namen im header in der Kundenansicht soll sich ein Fenster mit den
        Kontaktdaten einblenden." Die Daten kommen aus der geladenen Akte —
        sofort da und nur, was der Angemeldete hier sehen darf.
      */
      titelkarte: {
        beschriftung: 'Kontakt',
        inhalt: <Kontakt kontakt={kontaktAusAkte(kunde)} kundenart={kunde.kundenart} />,
      },
      handlungen: (
        <>
          <button type="button" className="knopf" onClick={() => rueckrufe.current.beiListe()}>
            Zur Liste
          </button>
          {kannZusammenfuehren && kunde.zusammengefuehrtIn === null && (
            <button
              type="button"
              className="knopf"
              onClick={() => rueckrufe.current.beiZusammenfuehren?.()}
            >
              Dublette zusammenführen
            </button>
          )}
          {/* RNG-11: Gutschrift ohne Bezug auf eine Rechnung, etwa aus Kulanz. */}
          {darfFreieGutschrift && (
            <button type="button" className="knopf" onClick={() => setzeFreieGutschrift(true)}>
              Freie Gutschrift …
            </button>
          )}
          {kannAuftrag && (
            <button type="button" className="knopf" onClick={() => rueckrufe.current.beiNeuerAuftrag?.()}>
              Neuer Auftrag
            </button>
          )}
          {/* BUC-75, KUN-17: zuerst der Auftrag, dann die Rechnung — nicht für gesperrte Kunden. */}
          {kannRechnung && !kunde.gesperrt && (
            <button type="button" className="knopf" onClick={() => rueckrufe.current.beiNeueRechnung?.()}>
              Neue Rechnung
            </button>
          )}
          {/* KUN-17: Gesperrt heißt: Angebot und Rechnung sind gesperrt. */}
          {kannAngebot && !kunde.gesperrt && (
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() => rueckrufe.current.beiNeuesAngebot?.()}
            >
              Neues Angebot
            </button>
          )}
        </>
      ),
    });
  }, [
    kunde, vorgaenge, ansprechpartner, beiKopf, kannZusammenfuehren, kannAngebot, kannAuftrag,
    kannRechnung,
    darfFreieGutschrift,
  ]);

  const sichern = useCallback(
    async (feld: string, wert: string | null): Promise<void> => {
      if (kunde === undefined) return;
      try {
        const { stand } = await feldSichern(kunde.id, feld, wert, kunde.stand);
        setzeKunde((k) => (k === undefined ? k : { ...k, [feld]: wert, stand }));
        setzeFehler(undefined);
        setzeRueckmeldung(`${feld} gesichert.`);
      } catch (ursache) {
        const f = ursache as ApiFehler;
        setzeFehler(f);
        // ERR-13: Bei einem Konflikt wird NICHT ueberschrieben. Der Bestand
        // wird neu geholt, damit der Anwender den anderen Wert sieht — und
        // ausdruecklich NICHT ueber `laden()`, das die Meldung wegraeumte.
        if (f.kennung === 'gleichzeitige_bearbeitung') {
          try {
            setzeKunde(await holeAkte(id));
          } catch {
            // Scheitert auch das Nachladen, bleibt die Konfliktmeldung.
          }
        }
      }
    },
    [kunde, id],
  );

  if (kunde === undefined) {
    const anzeigefehler = fehler ?? ladefehler;
    return (
      <>
        {anzeigefehler !== undefined && (
          <>
            <Meldung fehler={anzeigefehler} />
            <div className="knopfreihe">
              <button type="button" className="knopf" onClick={beiListe}>
                Zurück zur Kundenliste
              </button>
            </div>
          </>
        )}
        {/* Bis die Antwort uebernommen ist, steht weiter das Skelett. */}
        <Ladeanzeige zustand={geladen} was="Die Akte" skelett={<Aktenskelett />}>
          <Aktenskelett />
        </Ladeanzeige>
      </>
    );
  }

  const belegzahl =
    vorgaenge === undefined
      ? undefined
      : (vorgaenge.angebote?.length ?? 0)
        + (vorgaenge.auftraege?.length ?? 0)
        + (vorgaenge.rechnungen?.length ?? 0);

  const guthabenposten = vorgaenge?.offenePosten?.filter((p) => p.art === 'guthaben') ?? [];

  return (
    <>
      {meldung !== undefined && (
        <div className="streifen streifen--info" role="status">
          {meldung}
        </div>
      )}

      {/* M-12, Zustand "Zusammengefuehrte Dublette": 30 Tage ruecknehmbar (KUN-25, SYS-16). */}
      {kunde.zusammengefuehrtIn !== null && (
        <div className="warnung" role="status">
          Dieser Datensatz wurde zusammengeführt und zeigt auf einen anderen Kunden. Er erscheint
          nicht mehr in Liste und Suche.{' '}
          {/*
            Gewöhnlicher Knopf und kein Eingriff: Die beiden lassen sich danach
            wieder zusammenführen. Gefragt wird trotzdem (CRM-104) — der
            Datensatz steht sofort wieder in Liste und Suche.
          */}
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => {
              setzeNachfrage({ art: 'ruecknahme' });
            }}
          >
            Zusammenführung zurücknehmen …
          </button>
        </div>
      )}

      {/* DAT-12, BUC-145 (AeA-64): ein Guthaben erstatten oder mit einer anderen Forderung verrechnen. */}
      {darfBuchen && guthabenposten.length > 0 && (
        <section className="seitenblock" aria-labelledby="kunde-guthaben">
          <h2 id="kunde-guthaben" className="seitenblock__titel">
            Guthaben
          </h2>
          <ul className="liste">
            {guthabenposten.map((p) => (
              <li key={p.rechnung}>
                <p>{`${alsEuro(p.offenerBetrag.replace('-', ''))} aus ${p.rechnungsnummer}`}</p>
                <div className="knopfreihe">
                  <Guthabenhandlung
                    posten={p.rechnung}
                    rechnungsnummer={p.rechnungsnummer}
                    beiErledigt={(text) => {
                      setzeRueckmeldung(text);
                      void laden();
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {freieGutschrift && (
        <FreieGutschrift
          kunde={kunde.id}
          kundenname={kunde.name}
          beiSchliessen={() => setzeFreieGutschrift(false)}
          beiAngelegt={(neu) => {
            setzeFreieGutschrift(false);
            if (beiRechnung !== undefined) beiRechnung(neu);
            else setzeRueckmeldung('Freie Gutschrift als Entwurf angelegt.');
          }}
        />
      )}

      {nachfrage?.art === 'ruecknahme' && (
        <Rueckfrage
          frage="Zusammenführung zurücknehmen?"
          unterzeile={`${kunde.kundennummer} · ${kunde.name}`}
          tat="Zurücknehmen"
          beiBestaetigen={async () => {
            await zusammenfuehrungRuecknehmen(kunde.id);
            setzeNachfrage(null);
            setzeRueckmeldung('Die Zusammenführung wurde zurückgenommen.');
            // Scheitert das Nachladen, steht das auf der Seite — die Rücknahme ist geschehen.
            await laden();
          }}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        >
          {/* KUN-25, SYS-16: rücknehmbar binnen 30 Tagen; die Frist prüft die API. */}
          <p>
            {kunde.kundennummer} steht danach wieder als eigener Kunde in Liste und Suche und zeigt
            nicht mehr auf den führenden Datensatz.
          </p>
        </Rueckfrage>
      )}

      {nachfrage?.art === 'ausgeschieden' && (
        <Rueckfrage
          frage="Ansprechpartner als ausgeschieden führen?"
          unterzeile={nachfrage.name}
          tat="Ausgeschieden"
          beiBestaetigen={async () => {
            await kontaktBeenden(kunde.id, nachfrage.kontakt);
            setzeNachfrage(null);
            await laden();
          }}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        >
          {/* KUN-08, DAT-25: Er bleibt mit Austrittsdatum stehen und wird nicht gelöscht. */}
          <p>
            {nachfrage.name} steht nicht mehr unter den Ansprechpartnern zur Wahl. Belege mit
            diesem Namen bleiben lesbar. Zurücknehmen lässt sich das in der Akte nicht.
          </p>
        </Rueckfrage>
      )}

      {/* KUN-17 */}
      {kunde.gesperrt && (
        <div className="warnung" role="status">
          Dieser Kunde ist gesperrt: {kunde.sperrgrund ?? 'ohne Angabe'}. Angebot und Rechnung sind
          gesperrt.
        </div>
      )}

      {fehler !== undefined && <Meldung fehler={fehler} />}
      <p role="status" className="nur-vorlesen">
        {rueckmeldung}
      </p>

      <Aktenrumpf
        haupt={
          <>
            <Reiterleiste
              beschriftung="Bereiche der Kundenakte"
              gewaehlt={reiter}
              beiWahl={setzeReiter}
              reiter={[
                { kennung: 'details', beschriftung: 'Details' },
                {
                  kennung: 'kontakte',
                  beschriftung: 'Kontakte',
                  zahl: kunde.kontakte.filter((k) => !k.ausgeschieden).length,
                },
                { kennung: 'konditionen', beschriftung: 'Konditionen' },
                { kennung: 'belege', beschriftung: 'Belege', zahl: belegzahl },
                { kennung: 'buchungen', beschriftung: 'Buchungen', zahl: vorgaenge?.buchungen?.length },
                { kennung: 'dokumente', beschriftung: 'Dokumente' },
                ...(kommunikation === undefined
                  ? []
                  : [{
                    kennung: 'kommunikation' as const,
                    beschriftung: 'Kommunikation',
                    zahl: vorgaenge?.kommunikation?.length,
                  }]),
                ...(zeigtWerte
                  ? [{ kennung: 'gutscheinwerte' as const, beschriftung: 'Gutscheinwerte', zahl: wertezahl }]
                  : []),
              ]}
            />

            <div role="tabpanel" id={`tafel-${reiter}`} aria-labelledby={`reiter-${reiter}`}>
              {reiter === 'details' && (
                <div className="tafel__abschnitte">
                  <Abschnitt titel="Stammdaten">
                    <Feldraster>
                      {kunde.kundenart !== 'privat' && (
                        <Feldzeile
                          beschriftung="Firmierung"
                          kennung="akte-firmierung"
                          wert={kunde.firmierung}
                          beiSichern={(w) => sichern('firmierung', w)}
                        />
                      )}
                      <Feldzeile beschriftung="Kundennummer" wert={kunde.kundennummer} />
                      <Feldzeile
                        beschriftung="Rechtsform"
                        kennung="akte-rechtsform"
                        wert={kunde.rechtsform}
                        beiSichern={(w) => sichern('rechtsform', w)}
                      />
                      <Feldzeile
                        beschriftung="Branche"
                        kennung="akte-branche"
                        wert={kunde.branche}
                        beiSichern={(w) => sichern('branche', w)}
                      />
                      <Feldzeile
                        beschriftung="Herkunft"
                        kennung="akte-herkunft"
                        wert={kunde.herkunft}
                        hilfe="Woher der Kontakt kam — Telefon, Portal, Empfehlung, Messe, Website."
                        beiSichern={(w) => sichern('herkunft', w)}
                      />
                      <Feldzeile beschriftung="Betreuung" wert={kunde.betreuerName} />
                      {/* BUC-98 */}
                      {kunde.kundenart === 'privat' && (
                        <Mitgliedsfeld kunde={kunde} beiSichern={sichern} beiMeldung={setzeRueckmeldung} />
                      )}
                      {kunde.kundenart !== 'privat' && bgKennzeichen !== undefined && (
                        <Kostentraegerzeile
                          kunde={kunde.id}
                          darfVerwalten={bgKennzeichen.darfVerwalten}
                          beiMeldung={setzeRueckmeldung}
                          beiGeaendert={() => void laden()}
                        />
                      )}
                    </Feldraster>
                    <p className="hilfe">
                      Der Stift öffnet ein Feld; es wird beim Verlassen gesichert. Es gibt keinen
                      Speichern-Knopf.
                    </p>
                  </Abschnitt>

                  <Abschnitt titel="Anschrift und Kennungen">
                    <Feldraster>
                      <Feldzeile
                        beschriftung="USt-IdNr."
                        wert={
                          kunde.ustidnr === null
                            ? null
                            : `${kunde.ustidnr} (${
                                kunde.ustidnrStatus === 'offen'
                                  ? 'Prüfung offen'
                                  : kunde.ustidnrStatus === 'gueltig'
                                    ? 'geprüft'
                                    : 'ungültig'
                              })`
                        }
                      />
                      {kunde.leitwegId !== null && (
                        <Feldzeile beschriftung="Leitweg-ID" wert={kunde.leitwegId} />
                      )}
                      {kunde.adressen.map((a) => (
                        <Feldzeile
                          key={a.id}
                          beschriftung={`${ADRESSTYPNAME[a.typ] ?? a.typ}${a.standard ? ' · Standard' : ''}`}
                          wert={`${a.strasse}, ${a.plz} ${a.ort}`}
                        />
                      ))}
                    </Feldraster>
                  </Abschnitt>

                  <Kommunikation
                    kunde={kunde.id}
                    kanaele={kunde.kanaele}
                    beiAenderung={laden}
                    beiFehler={setzeFehler}
                  />

                  {/*
                    NEBEN DER KOMMUNIKATION UND NICHT IN EINEM EIGENEN REITER
                    (LEG-12): Wer die Adressen pflegt, entscheidet im selben
                    Handgriff, ob er den Kunden anschreiben darf.
                  */}
                  <Werbeeinwilligung
                    kunde={kunde}
                    beiAenderung={() => void laden()}
                    beiMeldung={setzeRueckmeldung}
                  />
                </div>
              )}

              {reiter === 'gutscheinwerte' && zeigtWerte && (
                <div className="tafel__abschnitte">
                  <Abschnitt titel="Gutscheinwerte">
                    {/* CRM-106, ÄA-63: Pflege ab Tablet, auf dem Telefon lesbar. */}
                    <AbTablet teil>
                      <Gutscheinwerte
                        kunde={kunde.id}
                        darfVerwalten={bgKennzeichen.darfVerwalten}
                        beiListe={zaehleWerte}
                      />
                    </AbTablet>
                  </Abschnitt>
                </div>
              )}

              {reiter === 'kontakte' && (
                <div className="tafel__abschnitte">
                  <div className="personenraster">
                    {kunde.kontakte.length === 0 && (
                      <p className="feld__hilfe">Noch kein Ansprechpartner erfasst.</p>
                    )}
                    {kunde.kontakte.map((k) => {
                      const name = `${k.person.vorname !== null ? `${k.person.vorname} ` : ''}${k.person.nachname}`;
                      return (
                        <Personenkaertchen
                          key={k.id}
                          kuerzel={kuerzel(name)}
                          name={
                            <KundeVerweis kundeId={kunde.id} ansprechpartnerId={k.id} zurAkte={zurKunde}>
                              {name}
                            </KundeVerweis>
                          }
                          zeilen={[
                            ...(k.funktion === null ? [] : [k.funktion]),
                            ...k.kanaele.map((c) => c.wert),
                          ]}
                          marken={
                            <>
                              {k.rechnungsempfaenger && (
                                <span className="marke">Rechnungsempfänger</span>
                              )}
                              {/*
                                LEG-12 (0319): der in DIESER Firma wirksame Stand.
                                Ein Widerspruch der Firma gilt für alle
                                Ansprechpartner; sein privater Stand wirkt hier
                                nicht. Die Regel steht in fach.werbestand_wirksam.
                              */}
                              {k.werbestand === 'widerspruch' && (
                                <span className="marke marke--krit">
                                  {k.werbestandVon === 'firma'
                                    ? 'Werbewiderspruch (Firma)'
                                    : 'Werbewiderspruch'}
                                </span>
                              )}
                              {k.werbestand === null && (
                                <span className="marke marke--inaktiv">ohne Werbeerlaubnis</span>
                              )}
                              {k.ausgeschieden && (
                                <span className="marke marke--inaktiv">ausgeschieden</span>
                              )}
                            </>
                          }
                          handlung={
                            k.ausgeschieden ? undefined : (
                              <>
                                <button
                                  type="button"
                                  className="knopf knopf--klein"
                                  aria-expanded={werbeKontakt === k.id}
                                  onClick={() => {
                                    setzeWerbeKontakt(werbeKontakt === k.id ? null : k.id);
                                  }}
                                >
                                  Werbestand …
                                </button>{' '}
                                {/*
                                  CRM-104: ein Eingriff mit Rückfrage — die Akte
                                  kennt keinen Weg zurück, der Austritt gilt sofort.
                                */}
                                <button
                                  type="button"
                                  className="knopf knopf--eingriff knopf--klein"
                                  onClick={() => {
                                    setzeNachfrage({ art: 'ausgeschieden', kontakt: k.id, name });
                                  }}
                                >
                                  Ausgeschieden …
                                </button>
                              </>
                            )
                          }
                        />
                      );
                    })}
                  </div>
                  <p className="feld__hilfe">
                    Ein ausgeschiedener Ansprechpartner bleibt stehen: Ein Angebot aus dem Vorjahr
                    trägt seinen Namen, und der Beleg muss lesbar bleiben.
                  </p>
                  {(() => {
                    const k = kunde.kontakte.find((c) => c.id === werbeKontakt && !c.ausgeschieden);
                    if (k === undefined) return null;
                    const name = `${k.person.vorname !== null ? `${k.person.vorname} ` : ''}${k.person.nachname}`;
                    const eigener = k.eigenerWerbestand;
                    return (
                      <Werbeabschnitt
                        key={k.id}
                        titel={`Werbliche Ansprache von ${name} bei dieser Firma`}
                        zusatz={
                          eigener === null
                            ? 'nichts eigenes dokumentiert'
                            : `seit ${new Date(eigener.seit).toLocaleDateString('de-DE')}`
                        }
                        unterzeile={`${name} bei dieser Firma`}
                        vorgabe={eigener?.stand ?? 'einwilligung'}
                        absenden={(stand, quelle, bemerkung) =>
                          werbestandAmAnsprechpartnerSetzen(k.id, stand, quelle, bemerkung)
                        }
                        beiAenderung={() => void laden()}
                        beiMeldung={setzeRueckmeldung}
                      >
                        <Feldraster>
                          <Feldzeile
                            beschriftung="Gilt hier"
                            wert={
                              k.werbestand === null
                                ? OHNE_WERBESTAND
                                : `${WERBESTANDTEXT[k.werbestand]}${k.werbestandVon === 'firma' ? ' (Stand der Firma)' : ''}`
                            }
                          />
                          <Feldzeile
                            beschriftung="Sein Stand hier"
                            wert={eigener === null ? '—' : WERBESTANDTEXT[eigener.stand]}
                          />
                          {eigener !== null && <Feldzeile beschriftung="Quelle" wert={eigener.quelle} />}
                          {eigener !== null && eigener.bemerkung !== null && (
                            <Feldzeile beschriftung="Bemerkung" wert={eigener.bemerkung} />
                          )}
                        </Feldraster>
                      </Werbeabschnitt>
                    );
                  })()}
                  <NeuerKontakt
                    kunde={kunde.id}
                    beiAngelegt={() => {
                      void laden();
                      setzeRueckmeldung('Ansprechpartner angelegt.');
                    }}
                    beiFehler={setzeFehler}
                  />
                </div>
              )}

              {reiter === 'konditionen' && kunde.kondition !== null && (
                <Konditionen
                  kunde={kunde.id}
                  kondition={kunde.kondition}
                  beiGesichert={laden}
                  beiFehler={setzeFehler}
                />
              )}

              {/*
                * AUF-06, RNG-08: die Belege des Kunden, beidseitig verfolgbar.
                * CRM-106: Auf dem Telefon steht jeder Beleg als Karte mit Nummer,
                * Betreff, Betrag und Status (Rechnungen: Fälligkeit). Belegdatum und
                * Gültigkeit stehen im Beleg, eine Tippberührung weiter.
                */}
              {reiter === 'belege' && (
                <div className="tafel__abschnitte">
                  <Abschnitt titel="Angebote" zusatz={vorgaenge?.angebote?.length}>
                    {vorgaenge?.angebote === undefined ? (
                      <Unlesbar was="Angebote" />
                    ) : vorgaenge.angebote.length === 0 ? (
                      <p className="feld__hilfe">Noch kein Angebot.</p>
                    ) : (
                      <div className="tabellenhuelle tabellenhuelle--telefon">
                        <table className="liste__tabelle tabelle--karten">
                          <thead>
                            <tr>
                              <th scope="col" className="liste__spalte--knapp">Nummer</th>
                              <th scope="col">Betreff</th>
                              <th scope="col" className="spalte--nicht-telefon">Datum</th>
                              <th scope="col" className="spalte--nicht-telefon">Gültig bis</th>
                              <th scope="col" className="zahl">Betrag</th>
                              <th scope="col">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vorgaenge.angebote.map((a) => (
                              <tr key={a.id}>
                                <td className="liste__spalte--knapp" data-spalte="Nummer">
                                  {beiAngebot === undefined ? (
                                    a.angebotsnummer
                                  ) : (
                                    <button type="button" className="liste__oeffnen liste__nummer" onClick={() => beiAngebot(a.id)}>
                                      {a.angebotsnummer}
                                    </button>
                                  )}
                                </td>
                                <td data-spalte="Betreff">{a.betreff}</td>
                                <td className="liste__einzeilig spalte--nicht-telefon">{langTag(a.belegdatum)}</td>
                                <td className="liste__einzeilig spalte--nicht-telefon">{langTag(a.gueltigBis)}</td>
                                <td className="zahl" data-spalte="Betrag">{alsEuro(a.belegbrutto)}</td>
                                <td data-spalte="Status">
                                  <span className={markenklasse(ANGEBOTSMARKE[a.status])}>
                                    {ANGEBOTSSTATUSNAME[a.status]}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Abschnitt>

                  <Abschnitt titel="Aufträge" zusatz={vorgaenge?.auftraege?.length}>
                    {vorgaenge?.auftraege === undefined ? (
                      <Unlesbar was="Aufträge" />
                    ) : vorgaenge.auftraege.length === 0 ? (
                      <p className="feld__hilfe">Noch kein Auftrag.</p>
                    ) : (
                      <div className="tabellenhuelle tabellenhuelle--telefon">
                        <table className="liste__tabelle tabelle--karten">
                          <thead>
                            <tr>
                              <th scope="col" className="liste__spalte--knapp">Nummer</th>
                              <th scope="col">Betreff</th>
                              <th scope="col" className="spalte--nicht-telefon">Datum</th>
                              <th scope="col" className="zahl">Betrag</th>
                              <th scope="col">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vorgaenge.auftraege.map((a) => (
                              <tr key={a.id}>
                                <td className="liste__spalte--knapp" data-spalte="Nummer">
                                  {beiAuftrag === undefined ? (
                                    a.auftragsnummer
                                  ) : (
                                    <button type="button" className="liste__oeffnen liste__nummer" onClick={() => beiAuftrag(a.id)}>
                                      {a.auftragsnummer}
                                    </button>
                                  )}
                                </td>
                                <td data-spalte="Betreff">{a.betreff}</td>
                                <td className="liste__einzeilig spalte--nicht-telefon">{langTag(a.belegdatum)}</td>
                                <td className="zahl" data-spalte="Betrag">{alsEuro(a.belegbrutto)}</td>
                                <td data-spalte="Status">
                                  <span className={markenklasse(AUFTRAGSMARKE[a.status])}>
                                    {AUFTRAGSSTATUSNAME[a.status]}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Abschnitt>

                  <Abschnitt titel="Rechnungen" zusatz={vorgaenge?.rechnungen?.length}>
                    {vorgaenge?.rechnungen === undefined ? (
                      <Unlesbar was="Rechnungen" />
                    ) : vorgaenge.rechnungen.length === 0 ? (
                      <p className="feld__hilfe">Noch keine Rechnung.</p>
                    ) : (
                      <div className="tabellenhuelle tabellenhuelle--telefon">
                        <table className="liste__tabelle tabelle--karten">
                          <thead>
                            <tr>
                              <th scope="col" className="liste__spalte--knapp">Nummer</th>
                              <th scope="col">Betreff</th>
                              <th scope="col" className="spalte--nicht-telefon">Datum</th>
                              <th scope="col">Fällig</th>
                              <th scope="col" className="zahl">Betrag</th>
                              <th scope="col">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vorgaenge.rechnungen.map((r) => (
                              <tr key={r.id}>
                                <td className="liste__spalte--knapp" data-spalte="Nummer">
                                  {beiRechnung === undefined ? (
                                    (r.rechnungsnummer ?? 'Entwurf')
                                  ) : (
                                    <button type="button" className="liste__oeffnen liste__nummer" onClick={() => beiRechnung(r.id)}>
                                      {r.rechnungsnummer ?? 'Entwurf'}
                                    </button>
                                  )}
                                </td>
                                <td data-spalte="Betreff">{r.betreff}</td>
                                <td className="liste__einzeilig spalte--nicht-telefon">{langTag(r.belegdatum)}</td>
                                <td className="liste__einzeilig" data-spalte="Fällig">{langTag(r.faelligAm)}</td>
                                <td className="zahl" data-spalte="Betrag">{alsEuro(r.belegbrutto)}</td>
                                <td data-spalte="Status">
                                  <span className={markenklasse(RECHNUNGSMARKE[r.status])}>
                                    {RECHNUNGSSTATUSNAME[r.status]}
                                  </span>
                                  {r.status === 'festgeschrieben' && (
                                    <span className={markenklasse(ZAHLUNGSMARKE[r.zahlungsstand])}>
                                      {ZAHLUNGSSTANDNAME[r.zahlungsstand]}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Abschnitt>
                </div>
              )}

              {/*
                * BUC-80: die Buchungen des Kunden aus der Buchungssuche; CRM-106: als Karte auf dem Telefon.
                * BUC-122 (Auftrag CRM-140): der Betrag der Buchungsakte statt der Teilnehmerzahl — auch storniert.
                */}
              {reiter === 'buchungen' &&
                (vorgaenge?.buchungen === undefined ? (
                  <Unlesbar was="Buchungen" />
                ) : vorgaenge.buchungen.length === 0 ? (
                  <p className="feld__hilfe">Dieser Kunde hat keine Buchung.</p>
                ) : (
                  <div className="tabellenhuelle tabellenhuelle--telefon">
                    <table className="liste__tabelle tabelle--karten">
                      <thead>
                        <tr>
                          <th scope="col">Datum</th>
                          <th scope="col">Training</th>
                          <th scope="col" className="zahl">Betrag</th>
                          <th scope="col">Buchung</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vorgaenge.buchungen.map((b) => (
                          <tr key={b.id}>
                            <td className="liste__einzeilig" data-spalte="Datum">{langTag(b.beginn)}</td>
                            <td data-spalte="Training">{b.produkt}</td>
                            <td className="zahl" data-spalte="Betrag">{alsEuro(b.betrag)}</td>
                            <td data-spalte="Buchung">
                              {beiBuchung === undefined ? (
                                b.buchungsnummer
                              ) : (
                                <button
                                  type="button"
                                  className="liste__oeffnen liste__nummer"
                                  onClick={() => beiBuchung(b.buchungsnummer)}
                                >
                                  {b.buchungsnummer}
                                </button>
                              )}
                            </td>
                            <td data-spalte="Status">
                              <span
                                className={markenklasse(
                                  b.status === 'storniert' || b.status === 'abgesagt'
                                    ? 'stillgelegt'
                                    : 'abschluss',
                                )}
                              >
                                {b.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

              {/*
                * SUP-11, SUP-06 (Auftrag CRM-177): die E-Mail-Vorgänge des Kunden, auch die eines
                * zusammengeführten; die Anhänge stehen in der Vorgangsansicht des Posteingangs.
                */}
              {reiter === 'kommunikation' && kommunikation !== undefined &&
                (vorgaenge?.kommunikation === undefined ? (
                  <Unlesbar was="E-Mail-Vorgänge" />
                ) : vorgaenge.kommunikation.length === 0 ? (
                  <p className="feld__hilfe">Mit diesem Kunden gibt es keinen E-Mail-Vorgang.</p>
                ) : (
                  <div className="tabellenhuelle tabellenhuelle--telefon">
                    <table className="liste__tabelle tabelle--karten">
                      <thead>
                        <tr>
                          <th scope="col">Letzte Nachricht</th>
                          <th scope="col">Vorgang</th>
                          <th scope="col">Betreff</th>
                          <th scope="col">Absender</th>
                          <th scope="col" className="zahl">Nachrichten</th>
                          <th scope="col" className="zahl">Anhänge</th>
                          <th scope="col">Zuordnung</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vorgaenge.kommunikation.map((v) => (
                          <tr key={v.id}>
                            <td className="liste__einzeilig" data-spalte="Letzte Nachricht">
                              {langTag(v.letzteNachrichtAm)}
                            </td>
                            <td data-spalte="Vorgang">
                              <button
                                type="button"
                                className="liste__oeffnen liste__nummer"
                                data-auch-telefon
                                onClick={() => kommunikation.beiVorgang(v.id, v.postfach)}
                              >
                                {v.nummer}
                              </button>
                            </td>
                            <td data-spalte="Betreff">{v.betreff}</td>
                            <td data-spalte="Absender">{v.absendername ?? v.absender}</td>
                            <td className="zahl" data-spalte="Nachrichten">{v.nachrichten}</td>
                            <td className="zahl" data-spalte="Anhänge">{v.anhaenge}</td>
                            {/* SUP-09: eine unsichere Zuordnung fällt auch in der Akte auf. */}
                            <td data-spalte="Zuordnung">
                              <Zuordnungsmarke zuordnung={v.zuordnung} sicherheit={v.sicherheit} />
                            </td>
                            <td data-spalte="Status">{statusmarke(v.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

              {reiter === 'dokumente' && (
                <p className="hinweiszeile">
                  {/* KUN-18, SYS-07 */}
                  Die Dokumentenablage folgt mit der Dateiablage.
                </p>
              )}
            </div>
          </>
        }
        seite={
          <>
            <div className="akte__seitentitel">
              <span>Aktivität</span>
            </div>
            {wiedervorlagen !== undefined && (
              <AktenWiedervorlagen kunde={id} ich={wiedervorlagen.ich} darfAendern={wiedervorlagen.darfAendern} />
            )}
            {/*
              KUN-14 AUS DEN VORGÄNGEN (ÄA-41). Anrufe, E-Mails und Aufgaben zu
              erfassen gibt es nicht — offener Punkt im Änderungsantrag.
            */}
            <Verlauf
              titel="Nächste Schritte"
              zusatz={`${schritte.length} offen`}
              eintraege={schritte}
              leer={vorgaenge === undefined ? 'Wird geladen …' : 'Nichts mit Frist.'}
            />
            <Verlauf
              titel="Verlauf"
              eintraege={verlauf}
              leer={
                vorgaenge === undefined
                  ? 'Wird geladen …'
                  : `Noch kein Vorgang. Kunde seit ${langTag(kunde.angelegtAm)}.`
              }
            />
          </>
        }
      />
    </>
  );
}
